import './AccountPopoverConfig';
import { Icon } from '@iconify/react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Divider, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { useRef, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import AccountBrief from './AccountPopOverComponents/AccountBrief';
import AccountInfo from './AccountPopOverComponents/AccountInfo';
import {
  InitialsStyled,
  ListItemIconStyle,
  MenuItemTextStyled,
  MIconButtonStyled,
} from './styles/AccountPopoverStyles';
import { userMajors } from '../../cache/cache';
import { invalidMajor } from '../../cache/learner/invalidMajorReactiveVar';
import AtomFlex from '../../components/atoms/AtomFlex';
import ModalTwoStepAuthentication from '../../components/generics/ModalTwoStepAuthentication/ModalTwoStepAuthentication';
import AuthenticatorIcon from '../../components/icons/svg/AuthenticatorIcon';
import { styleConfigs } from '../../components/resources/styleConfigs';
import MenuPopover from '../../components/template-minimal/MenuPopoverComponent/MenuPopover';
import SvgIconStyle from '../../components/template-minimal/SvgIconStyle';
import icons from '../../constants/icons';
import { INVALID_MAJOR_ID } from '../../constants/invalidMajorId';
import { FeatureFlagsContext } from '../../contexts/FeatureFlagsContext';
import { ModalContext } from '../../contexts/ModalContext';
import userAuth from '../../hooks/userAuth';
import { Mixpanel } from '../../mixpanel/mixpanel';
import { MixpanelComponentsNames } from '../../mixpanel/MixpanelEvents/mixpanelComponentNames';
import { MixpanelEventsEnum } from '../../mixpanel/MixpanelEvents/mixpanelEventsInterface';
import { useLazyGetStudentMajors } from '../../operations/query/students/useGetStudentMajors';
import { PATH_DASHBOARD_STUDENT } from '../../routes/paths';
import { getMixpanelName } from '../../utils/getMixpanelName';
import { onEnterKey } from '../../utils/onEnterKey';
import { popInvalidMajors } from '../../utils/popInvalidMajor';
import { ROLE_ENUM } from '../../views/Auth/Login/interfaces/IGoogleUser';

type IAccountPopoverProps = {
  readonly openChangePasswordDialog: VoidFunction;
};

interface IMenuOptions {
  label: string;
  icon: JSX.Element;
  linkTo: string;
}

const getIcon = (icon: string): JSX.Element => <SvgIconStyle src={icon} sx={{ width: '100%', height: '100%' }} />;

const ICONS = {
  profile: getIcon(icons.PROFILE_ICON),
  password: getIcon(icons.PASSWORD_ICON),
  twoAuthentication: (
    <AtomFlex>
      <AuthenticatorIcon />
    </AtomFlex>
  ),
  logout: getIcon(icons.LOGOUT_ICON),
};

const MENU_OPTIONS: IMenuOptions[] = [
  { label: 'PROFILE', icon: ICONS.profile, linkTo: PATH_DASHBOARD_STUDENT.profile.information },
  { label: 'CHANGE_PASSWORD', icon: ICONS.password, linkTo: '#' },
  { label: 'TWO_AUTHENTICATION', icon: ICONS.twoAuthentication, linkTo: '#' },
  { label: 'LOGOUT', icon: ICONS.logout, linkTo: '#' },
];

export default function AccountPopover(props: IAccountPopoverProps): JSX.Element {
  const { openChangePasswordDialog } = props;
  const { logout, auth } = userAuth();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  const isLearner: boolean = auth?.userRole === ROLE_ENUM.STUDENT;
  const studentInitials = `${auth?.student?.name.replace(/\s/g, '')[0]}${
    auth?.student?.lastName.replace(/\s/g, '')[0]
  }`;
  const { t } = useTranslation('LearnerAccountPopover');
  const studentFullName = `${auth?.student?.name ?? ''} ${auth?.student?.lastName ?? ''}`.trim();
  const accountButtonAriaLabel = t('ACCOUNT_MENU_BUTTON_ARIA_LABEL', {
    name: studentFullName || t('GUEST_NAME'),
  });
  const { currentUser } = getAuth();
  const [fromKeyboard, setFromKeyboard] = useState(0);
  const { features } = useContext(FeatureFlagsContext);
  const smScreen: boolean = useMediaQuery(useTheme().breakpoints.down('sm'));
  const { modalOpen } = useContext(ModalContext);

  const openModalTwoStepAuthentication = (): void => {
    modalOpen({
      component: ModalTwoStepAuthentication,
      desktopWidth: styleConfigs.dimensions.width615,
      mobileHeight: styleConfigs.dimensions.height100dvh,
    });
  };

  const handleOptions = (): IMenuOptions[] => {
    let options = currentUser?.providerData?.find(({ providerId }) => providerId === 'password')
      ? MENU_OPTIONS
      : MENU_OPTIONS.filter(({ label }) => label !== 'CHANGE_PASSWORD');

    if (!features.qsemShowProfileDropdown2FA) {
      options = options.filter((item) => item.label !== 'TWO_AUTHENTICATION');
    }

    if (auth?.redirectLogoutUrl) {
      options = options.filter((item) => item.label !== 'LOGOUT');
    }

    return options;
  };

  const handleOpen = (): void => {
    setFromKeyboard(0);
    setOpen(true);
    Mixpanel.track(
      {
        action: MixpanelEventsEnum.PROFILE_POPOVER,
        data: {
          screen: getMixpanelName(pathname.concat(search)),
          component: MixpanelComponentsNames.ACCOUNT_POPOVER,
          action: 'click',
        },
      },
      isLearner,
    );
  };

  const handleClose = async (label?: string, linkTo?: string, canNavigate = false): Promise<void> => {
    if (label) {
      const screenName = pathname.concat(search);

      if (label === 'CHANGE_PASSWORD') {
        openChangePasswordDialog();
        Mixpanel.track(
          {
            action: MixpanelEventsEnum.CHANGE_PASSWORD,
            data: {
              screen: getMixpanelName(screenName),
              component: MixpanelComponentsNames.ACCOUNT_POPOVER,
              action: 'click',
            },
          },
          isLearner,
        );
      }
      if (label === 'TWO_AUTHENTICATION') {
        openModalTwoStepAuthentication();
      }
      if (label === 'PROFILE') {
        Mixpanel.track(
          {
            action: MixpanelEventsEnum.GO_TO_PROFILE,
            data: {
              screen: getMixpanelName(screenName),
              component: MixpanelComponentsNames.ACCOUNT_POPOVER,
              action: 'click',
            },
          },
          isLearner,
        );
        if (canNavigate) {
          navigate(linkTo ?? '/');
        }
      }
      if (label === 'LOGOUT') {
        await logout();
        navigate('/');
        Mixpanel.track(
          {
            action: MixpanelEventsEnum.LOGOUT,
            data: {
              screen: getMixpanelName(screenName),
              component: MixpanelComponentsNames.ACCOUNT_POPOVER,
              action: 'click',
            },
          },
          isLearner,
        );
      }
    }
    setOpen(false);
  };

  const handleKeyDownButton = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    const keyPressed = e.key;

    switch (keyPressed) {
      case 'ArrowRight':
        setFromKeyboard(1);
        break;

      case 'ArrowLeft':
        setFromKeyboard(1);
        break;

      case 'End':
        setFromKeyboard(1);
        break;

      case 'Home':
        setFromKeyboard(1);
        break;

      case 'Tab':
        setFromKeyboard(1);
        break;

      default:
        setFromKeyboard(0);

        break;
    }
  };

  const [getData, { data: majorResponse }] = useLazyGetStudentMajors(true);

  useEffect(() => {
    if (majorResponse?.getStudentMajors) {
      const invalidMajors =
        majorResponse.getStudentMajors.filter((major) => major.tblMajor?.id === INVALID_MAJOR_ID).length > 0 ||
        !majorResponse.getStudentMajors[0]?.tblMajor;

      invalidMajor(invalidMajors);

      const filteredMajors = popInvalidMajors(majorResponse.getStudentMajors);

      const majors = filteredMajors.map((item) => ({
        name: item.tblMajor?.name ?? '',
      }));

      userMajors(majors);
    }
  }, [majorResponse]);

  useEffect(() => {
    getData({
      variables: {
        studentId: auth?.student?.id ?? '',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MIconButtonStyled
        data-testid="pop_button"
        ref={anchorRef}
        onClick={handleOpen}
        onKeyDown={onEnterKey(handleKeyDownButton)}
        open={open ? 1 : 0}
        islearner={isLearner ? 1 : 0}
        fromkeyboard={fromKeyboard}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={accountButtonAriaLabel}
      >
        <InitialsStyled>{studentInitials}</InitialsStyled>
        {!smScreen && (
          <>
            <AccountBrief />
            <ExpandMore aria-hidden="true" />
          </>
        )}
      </MIconButtonStyled>

      <MenuPopover
        id="learner-MenuPopover"
        open={open}
        onClose={() => handleClose()}
        anchorEl={anchorRef.current}
        width={330}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <AccountInfo />
        </Box>
        <Box aria-label="Account options" role="menu">
          {handleOptions().map((option) => (
            <div key={option.label}>
              <Divider variant="middle" />
              <MenuItem
                key={option.label}
                to={option.linkTo}
                component={RouterLink}
                onKeyDown={onEnterKey(() => {
                  handleClose(option.label, option.linkTo, true);
                })}
                onClick={(event: any) => {
                  if (option.linkTo === '#') {
                    event.preventDefault();
                  }
                  handleClose(option.label);
                }}
                sx={{ typography: 'body2', py: 2.5, px: 2.5 }}
                tabIndex={0}
              >
                <Box
                  component={Icon}
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 2,
                  }}
                />
                <ListItemIconStyle>{option.icon}</ListItemIconStyle>
                <MenuItemTextStyled>{t(option.label)}</MenuItemTextStyled>
              </MenuItem>
            </div>
          ))}
        </Box>
      </MenuPopover>
    </>
  );
}
